import { ChevronDown, Loader2, MessageSquare, Sparkles } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { normalizeE164 } from '@/utils/phone'
import { parseError } from '../../lib/errors'
import { useCreateNewSmsDraft } from '../../models/salesLead'
import { type AircallNumber, NumberLabel } from './types'
import { useAircall } from './useAircall'

interface Props {
  phone?: string | null
  cell?: string | null
  // When provided, these numbers are used directly (e.g. from a lead contact);
  // otherwise they're derived from the legacy phone/cell fields.
  numbers?: AircallNumber[]
  // When a leadId is set, the dropdown offers "New AI draft".
  leadId?: string
  onCompose: (recipient: AircallNumber) => void
}

function resolveNumbers(phone: string | null | undefined, cell: string | null | undefined): AircallNumber[] {
  const phoneE164 = normalizeE164(phone)
  const cellE164 = normalizeE164(cell)
  const numbers: AircallNumber[] = []
  if (phoneE164) numbers.push({ label: NumberLabel.Phone, value: phoneE164 })
  if (cellE164 && cellE164 !== phoneE164) numbers.push({ label: NumberLabel.Cell, value: cellE164 })
  return numbers
}

function DisabledSmsButton({ tooltip }: { tooltip: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>
          <Button variant="outline" disabled aria-disabled="true">
            <MessageSquare className="size-4" />
            SMS
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}

export function SmsButton({ phone, cell, numbers: numbersProp, leadId, onCompose }: Props) {
  const aircall = useAircall()
  const createNewSmsDraft = useCreateNewSmsDraft()
  const numbers = numbersProp ?? resolveNumbers(phone, cell)

  if (numbers.length === 0) {
    return <DisabledSmsButton tooltip="No phone number on file" />
  }

  if (!aircall || aircall.status === 'disabled') {
    return <DisabledSmsButton tooltip="Aircall is not enabled for your account" />
  }

  if (!aircall.smsEnabled) {
    return <DisabledSmsButton tooltip="SMS not enabled for your Aircall number — set it in Django admin" />
  }

  const primary = numbers[0]
  const hasMultipleNumbers = numbers.length > 1
  const showDraftItem = !!leadId
  const isDrafting = createNewSmsDraft.isPending
  const btnClass = 'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 hover:text-sky-800'

  const onNewAiDraft = () => {
    if (!leadId) return
    createNewSmsDraft.mutate(
      { leadId },
      {
        onSuccess: () => toast.success('AI draft added to the activity feed'),
        onError: (err) => toast.error(parseError(err)),
      }
    )
  }

  // No dropdown needed: a single number and nothing else to offer.
  if (!hasMultipleNumbers && !showDraftItem) {
    return (
      <Button variant="outline" className={btnClass} onClick={() => onCompose(primary)}>
        <MessageSquare className="size-4" />
        SMS
      </Button>
    )
  }

  return (
    <div className="inline-flex">
      <Button
        variant="outline"
        className={`rounded-r-none border-r-0 ${btnClass}`}
        onClick={() => onCompose(primary)}
      >
        <MessageSquare className="size-4" />
        SMS{hasMultipleNumbers ? ` ${primary.label}` : ''}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            aria-label="More SMS options"
            className={`rounded-l-none px-2 ${btnClass}`}
            disabled={isDrafting}
          >
            {isDrafting ? <Loader2 className="size-4 animate-spin" /> : <ChevronDown className="size-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {hasMultipleNumbers && (
            <>
              <DropdownMenuLabel>Text</DropdownMenuLabel>
              {numbers.map((n) => (
                <DropdownMenuItem key={n.value} onSelect={() => onCompose(n)}>
                  {n.label} · {n.value}
                </DropdownMenuItem>
              ))}
            </>
          )}
          {hasMultipleNumbers && showDraftItem && <DropdownMenuSeparator />}
          {showDraftItem && (
            <DropdownMenuItem onSelect={onNewAiDraft} disabled={isDrafting}>
              <Sparkles className="mr-2 size-4" />
              New AI draft
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
